import { clsx } from 'clsx';

export type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'secondary';

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  className?: string;
}

const variantClasses: Record<BadgeVariant, string> = {
  default: 'bg-gray-500/20 text-gray-400',
  success: 'bg-green-500/20 text-green-400',
  warning: 'bg-amber-500/20 text-amber-400',
  danger: 'bg-crimson-500/20 text-crimson-400',
  info: 'bg-gold/20 text-gold',
  secondary: 'bg-amber-900/30 text-amber-300',
};

export function Badge({ children, variant = 'default', className }: BadgeProps) {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        variantClasses[variant],
        className
      )}
    >
      {children}
    </span>
  );
}

// Status badge presets
export function StatusBadge({ status }: { status: string }) {
  const statusConfig: Record<string, { label: string; variant: BadgeVariant }> = {
    // Movie/Event status
    draft: { label: 'Draft', variant: 'default' },
    published: { label: 'Published', variant: 'success' },
    archived: { label: 'Archived', variant: 'warning' },
    cancelled: { label: 'Cancelled', variant: 'danger' },
    completed: { label: 'Completed', variant: 'info' },
    // Inquiry status
    new: { label: 'New', variant: 'info' },
    in_progress: { label: 'In Progress', variant: 'warning' },
    resolved: { label: 'Resolved', variant: 'success' },
    spam: { label: 'Spam', variant: 'danger' },
    // Job status
    contacted: { label: 'Contacted', variant: 'warning' },
    interviewed: { label: 'Interviewed', variant: 'info' },
    hired: { label: 'Hired', variant: 'success' },
    rejected: { label: 'Rejected', variant: 'danger' },
    withdrawn: { label: 'Withdrawn', variant: 'default' },
  };

  const config = statusConfig[status] ?? { label: status, variant: 'default' as const };

  return <Badge variant={config.variant}>{config.label}</Badge>;
}
